import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Trophy } from 'lucide-react';
import { getTierForPoints } from '@/lib/loyaltyTiers';

// Circular avatar showing the customer's uploaded profile picture (or their
// first-initial fallback) with a small loyalty-tier trophy badge pinned to the
// bottom-right. The trophy colour + title reflect the customer's current tier,
// computed from their lifetime earned loyalty points.
//
// Pass a precomputed `tier` (from getTierForPoints) when rendering many avatars
// at once (e.g. a directory) so it skips the per-row ledger fetch.
export default function TierAvatar({ email, profilePictureUrl, tier, size = 64 }) {
  const { data: ledger = [] } = useQuery({
    queryKey: ['tier-avatar-loyalty', email],
    queryFn: () => base44.entities.LoyaltyLedger.filter({ customer_email: email }, '-created_date', 200),
    enabled: !!email && !tier,
  });

  const resolvedTier = tier || (() => {
    const lifetimeEarned = ledger
      .filter((r) => Number(r.points) > 0)
      .reduce((s, r) => s + Number(r.points), 0);
    return getTierForPoints(lifetimeEarned);
  })();

  const initial = ((profilePictureUrl ? '' : (email || '?')).charAt(0) || '?').toUpperCase();

  const badge = Math.round(size * 0.38);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {profilePictureUrl ? (
        <img
          src={profilePictureUrl}
          alt="Profile"
          className="w-full h-full object-cover rounded-full border border-white/10"
        />
      ) : (
        <div
          className="w-full h-full rounded-full bg-primary/10 flex items-center justify-center text-primary font-black border border-white/10"
          style={{ fontSize: Math.round(size * 0.4) }}
        >
          {initial}
        </div>
      )}

      {/* Loyalty tier trophy badge (bottom-right) */}
      <span
        title={`Loyalty tier: ${resolvedTier.name}`}
        className="absolute flex items-center justify-center rounded-full border-2 border-[#0d0d0f] shadow-lg"
        style={{
          width: badge,
          height: badge,
          bottom: -Math.round(badge * 0.18),
          right: -Math.round(badge * 0.18),
          backgroundColor: resolvedTier.color,
        }}
      >
        <Trophy
          className="text-black/80"
          style={{ width: Math.round(badge * 0.6), height: Math.round(badge * 0.6) }}
        />
      </span>
    </div>
  );
}